import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Inbox, Mail, Building2, CheckCircle2, XCircle, Trash2, Loader2,
  TrendingUp, Eye, Target, Send, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { id: 'new', label: 'Nouveau', tone: 'bg-accent/20 text-accent border-accent/40' },
  { id: 'contacted', label: 'Contacté', tone: 'bg-amber-500/20 text-amber-300 border-amber-400/40' },
  { id: 'qualified', label: 'Qualifié', tone: 'bg-purple-500/20 text-purple-300 border-purple-400/40' },
  { id: 'converted', label: 'Converti', tone: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40' },
  { id: 'dismissed', label: 'Refusé', tone: 'bg-slate-600/30 text-slate-400 border-slate-500/40' },
];

const STATUS_BY_ID = STATUSES.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

/**
 * Admin — Leads tab. Lists contact_leads + funnel KPI + status mutations.
 * Created 29/05/2026.
 */
export default function LeadsTab({ token }) {
  const [leads, setLeads] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }), [token]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/leads`, { headers });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.items || []);
      }
    } catch (err) { console.warn(err.message); }
    setLoading(false);
  }, [headers]);

  const fetchFunnel = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/demo/funnel?days=30`, { headers });
      if (res.ok) setFunnel(await res.json());
    } catch (err) { console.warn(err.message); }
  }, [headers]);

  useEffect(() => { fetchLeads(); fetchFunnel(); }, [fetchLeads, fetchFunnel]);

  const updateStatus = async (lead, status) => {
    setSavingId(lead.id);
    try {
      await fetch(`${API}/api/leads/${lead.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ status }),
      });
      await fetchLeads();
      await fetchFunnel();
    } catch (err) { alert(`Erreur : ${err.message}`); }
    setSavingId(null);
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Supprimer le lead ${lead.email} ?`)) return;
    setSavingId(lead.id);
    try {
      await fetch(`${API}/api/leads/${lead.id}`, { method: 'DELETE', headers });
      await fetchLeads();
      await fetchFunnel();
    } catch (err) { alert(`Erreur : ${err.message}`); }
    setSavingId(null);
  };

  const filteredLeads = useMemo(
    () => (filter === 'all' ? leads : leads.filter((l) => (l.status || 'new') === filter)),
    [leads, filter],
  );

  return (
    <div className="space-y-6" data-testid="leads-tab">
      <Header onRefresh={() => { fetchLeads(); fetchFunnel(); }} loading={loading} />

      {funnel && <FunnelBar funnel={funnel} />}

      <FilterBar
        leads={leads} filter={filter} setFilter={setFilter}
      />

      {filteredLeads.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <LeadsTable
          leads={filteredLeads} savingId={savingId}
          onStatusChange={updateStatus} onDelete={deleteLead}
        />
      )}
    </div>
  );
}

function Header({ onRefresh, loading }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2">
          <Inbox className="w-5 h-5 text-accent" /> Leads démo & contacts
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Prospects capturés via la démo publique (<code className="font-mono">/demo</code>) et les CTA contact.
        </p>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={loading}
        className="flex items-center gap-2 bg-accent hover:bg-accent-hover text-white px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
        data-testid="leads-refresh"
      >
        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Actualiser
      </button>
    </div>
  );
}

function FunnelBar({ funnel }) {
  const f = funnel.funnel;
  const r = funnel.rates;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="funnel-kpis">
      <FunnelTile icon={Eye} label="Bannière cliquée" value={f.banner_clicked} sub={`${funnel.days} derniers j.`} tone="text-accent" />
      <FunnelTile icon={Send} label="Démos lancées" value={f.demo_started} sub={`${r.banner_to_started}% conv.`} tone="text-purple-300" />
      <FunnelTile icon={Target} label="Démos terminées" value={f.demo_completed} sub={`${r.started_to_completed}% conv.`} tone="text-amber-300" />
      <FunnelTile icon={TrendingUp} label="Leads soumis" value={f.demo_submitted} sub={`${r.completed_to_submitted}% conv.`} tone="text-emerald-300" />
    </div>
  );
}

function FunnelTile({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className="px-3 py-3 rounded-lg bg-ink-800 border border-ink-600">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-mono">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className={`text-2xl font-black font-mono mt-1 ${tone}`}>{value}</p>
      <p className="text-[10px] text-slate-500 font-mono">{sub}</p>
    </div>
  );
}

function FilterBar({ leads, filter, setFilter }) {
  const counts = useMemo(() => leads.reduce((acc, l) => {
    const s = l.status || 'new';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {}), [leads]);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="leads-filter">
      <button
        type="button"
        onClick={() => setFilter('all')}
        className={`text-xs px-3 py-1.5 rounded-full border font-bold uppercase tracking-wider transition-colors ${
          filter === 'all' ? 'bg-ink-700 text-foreground border-ink-500' : 'bg-ink-800 text-slate-400 border-ink-600 hover:text-slate-200'
        }`}
        data-testid="leads-filter-all"
      >
        Tous ({leads.length})
      </button>
      {STATUSES.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => setFilter(s.id)}
          className={`text-xs px-3 py-1.5 rounded-full border font-bold uppercase tracking-wider transition-colors ${
            filter === s.id ? s.tone : 'bg-ink-800 text-slate-400 border-ink-600 hover:text-slate-200'
          }`}
          data-testid={`leads-filter-${s.id}`}
        >
          {s.label} ({counts[s.id] || 0})
        </button>
      ))}
    </div>
  );
}

function EmptyState({ filter }) {
  return (
    <div className="bg-ink-800 border border-ink-600 rounded-xl p-12 text-center" data-testid="leads-empty">
      <Inbox className="w-10 h-10 text-slate-600 mx-auto mb-3" />
      <p className="text-sm text-slate-400">
        {filter === 'all' ? 'Aucun lead pour le moment.' : `Aucun lead avec le statut « ${STATUS_BY_ID[filter]?.label || filter} ».`}
      </p>
      <p className="text-[11px] text-slate-500 mt-1">
        Partagez l&apos;URL <code className="font-mono">/demo</code> pour collecter vos premiers prospects.
      </p>
    </div>
  );
}

function LeadsTable({ leads, savingId, onStatusChange, onDelete }) {
  return (
    <div className="bg-ink-800 rounded-xl border border-ink-600 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-ink-700/30 border-b border-ink-600">
          <tr>
            <Th>Contact</Th>
            <Th>Organisation</Th>
            <Th>Statut</Th>
            <Th>Source</Th>
            <Th>Reçu</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <LeadRow key={lead.id} lead={lead} saving={savingId === lead.id} onStatusChange={onStatusChange} onDelete={onDelete} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = '' }) {
  return (
    <th className={`text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function LeadRow({ lead, saving, onStatusChange, onDelete }) {
  const status = STATUS_BY_ID[lead.status || 'new'];
  return (
    <tr className="border-b border-ink-600 hover:bg-ink-700/30 transition-colors" data-testid={`lead-row-${lead.id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Mail className="w-3 h-3 text-slate-500 flex-shrink-0" />
          <div className="min-w-0">
            <a href={`mailto:${lead.email}`} className="ph-no-capture text-slate-200 text-sm hover:text-accent block truncate">{lead.email}</a>
            {lead.name && <p className="text-[11px] text-slate-500 truncate">{lead.name}</p>}
          </div>
        </div>
        {lead.message && (
          <p className="text-[10px] text-slate-500 italic mt-1 pl-5 line-clamp-2" title={lead.message}>« {lead.message} »</p>
        )}
      </td>
      <td className="px-4 py-3">
        {lead.organisation ? (
          <span className="text-xs text-slate-300 flex items-center gap-1.5">
            <Building2 className="w-3 h-3 text-slate-500" /> {lead.organisation}
          </span>
        ) : (
          <span className="text-xs text-slate-600">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${status.tone}`}>
          {status.label}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{lead.source}</span>
      </td>
      <td className="px-4 py-3 text-xs text-slate-500 font-mono">
        {new Date(lead.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          {saving && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
          {lead.status !== 'contacted' && lead.status !== 'converted' && (
            <button
              type="button"
              onClick={() => onStatusChange(lead, 'contacted')}
              disabled={saving}
              className="text-[10px] px-2 py-1 rounded bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/40 text-amber-300 font-bold uppercase tracking-wider disabled:opacity-40 transition-colors"
              data-testid={`lead-mark-contacted-${lead.id}`}
              title="Marquer comme contacté"
            >
              Contacté
            </button>
          )}
          {lead.status !== 'converted' && (
            <button
              type="button"
              onClick={() => onStatusChange(lead, 'converted')}
              disabled={saving}
              className="text-[10px] px-2 py-1 rounded bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/40 text-emerald-300 font-bold uppercase tracking-wider disabled:opacity-40 transition-colors flex items-center gap-1"
              data-testid={`lead-mark-converted-${lead.id}`}
              title="Marquer comme converti"
            >
              <CheckCircle2 className="w-2.5 h-2.5" /> Converti
            </button>
          )}
          {lead.status !== 'dismissed' && (
            <button
              type="button"
              onClick={() => onStatusChange(lead, 'dismissed')}
              disabled={saving}
              className="text-[10px] px-2 py-1 rounded bg-slate-600/20 hover:bg-slate-600/30 border border-slate-500/40 text-slate-400 font-bold uppercase tracking-wider disabled:opacity-40 transition-colors flex items-center gap-1"
              data-testid={`lead-mark-dismissed-${lead.id}`}
              title="Refuser"
            >
              <XCircle className="w-2.5 h-2.5" /> Refusé
            </button>
          )}
          <button aria-label="Supprimer"
            type="button"
            onClick={() => onDelete(lead)}
            disabled={saving}
            className="text-[10px] p-1.5 rounded text-slate-500 hover:text-danger hover:bg-danger/10 disabled:opacity-40 transition-colors"
            data-testid={`lead-delete-${lead.id}`}
            title="Supprimer"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </td>
    </tr>
  );
}
